import React from 'react';
import { ArrowRight, FileText, Shield, Zap, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

// Apple-inspired color palette - kept consistent with main app
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)'
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  },
};

const MobileHeroSection = () => {
  return (
    <motion.div 
      className="min-h-[90vh] flex flex-col items-center justify-center px-5 pt-20 pb-16 relative overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        background: `radial-gradient(circle at 70% 20%, ${colors.accentLight} 0%, rgba(255, 255, 255, 0) 25%)`,
        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div className="text-center max-w-md mx-auto relative z-10 flex flex-col items-center">
        <motion.div
          className="flex justify-center mb-5 w-full"
          variants={itemVariants}
        >
          <img src="/6.png" alt="SignPact Logo" className="h-12" />
        </motion.div>
        
        <motion.h1 
          className="text-4xl font-bold mb-5" 
          style={{
            lineHeight: '1.1',
            fontWeight: 600
          }}
          variants={itemVariants}
        >
          <span style={{ color: '#333333' }}>Documents.</span>
          <span style={{ color: '#1e40af' }}>Simplified.</span> 
        </motion.h1>
        
        <motion.p 
          className="text-lg mb-8" 
          style={{
            color: colors.gray700, 
            lineHeight: '1.4',
            letterSpacing: '-0.01em',
            fontWeight: 400
          }}
          variants={itemVariants}
        >
    AI automates your entire contract workflow—from creation to signature. What used to take weeks now takes minutes.
    </motion.p>
            
        <div className="flex flex-row gap-3 w-full">
          {/* Primary Button - Book a Demo */}
          <motion.button 
            onClick={() => window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank')}
            className="flex-1 py-2.5 px-4 rounded-xl text-white font-medium flex items-center justify-center"
            style={{
              backgroundColor: colors.accent,
              color: colors.white,
              fontSize: '15px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.1), 0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            whileHover={{ 
              scale: 1.03, 
              boxShadow: "0 10px 15px -3px rgba(37, 99, 235, 0.1), 0 4px 6px -2px rgba(37, 99, 235, 0.05)" 
            }}
            whileTap={{ scale: 0.97 }}
            variants={itemVariants}
          >
            Book a Demo
            <ArrowRight size={14} className="ml-1.5" />
          </motion.button>

          {/* Secondary Button - See a Demo */}
          <motion.button 
            onClick={() => window.open('https://youtu.be/6gHYPspN5hY', '_blank')}
            className="flex-1 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center"
            style={{
              backgroundColor: colors.gray100,
              color: colors.gray700,
              fontSize: '15px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
            whileHover={{ 
              scale: 1.03, 
              backgroundColor: colors.gray200 
            }}
            whileTap={{ scale: 0.97 }}
            variants={itemVariants}
          >
            See a Demo
            <Eye size={14} className="ml-1.5" />
            
          </motion.button>
        </div>
      </div>
      
      {/* Document Collection Container */}
      <div className="relative w-full h-80 my-4">
        {/* Main Document - Primary focus */}
        <motion.div 
          className="absolute left-1/2 transform -translate-x-1/2 top-[10%] w-[80%] max-w-[280px] z-30"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="bg-white rounded-xl shadow-xl overflow-hidden mx-auto" 
              style={{
                boxShadow: '0 15px 30px -12px rgba(0, 0, 0, 0.25)', 
                border: '1px solid rgba(230, 230, 250, 0.7)',
              }}>
            {/* Document Header */}
            <div className="h-10 flex items-center px-4 border-b relative" 
                style={{
                  background: 'linear-gradient(120deg, #f8fafc 0%, #f1f5f9 100%)', 
                  borderColor: 'rgba(226, 232, 240, 0.8)'
                }}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"></div>
              <div className="flex space-x-1.5 mr-3">
                <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#ff5a5a'}}></div>
                <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#ffbc5a'}}></div>
                <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: '#34c759'}}></div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div className="flex items-center text-xs font-medium" style={{color: colors.gray700}}>
                  <div className="w-4 h-4 rounded-sm flex items-center justify-center mr-1.5" style={{backgroundColor: colors.accentLight}}>
                    <FileText size={9} style={{color: colors.accent}} />
                  </div>
                  Service Agreement
                </div>
              </div>
            </div>
            
            {/* Document Content - Simplified for mobile */}
            <div className="p-4 relative">
              <div className="mb-3 flex justify-between items-center">
                <div className="text-xs font-bold text-gray-800">SERVICE AGREEMENT</div>
                <div className="px-1.5 py-0.5 text-[10px] rounded-md flex items-center" 
                    style={{backgroundColor: 'rgba(239, 246, 255, 0.6)', color: colors.accent}}>
                  E-Signature Ready
                </div>
              </div>

              {/* Document Text - Much simpler for mobile */}
              <div className="space-y-3 text-xs" style={{color: colors.gray700}}>
                <div className="border-l-2 pl-2" style={{borderColor: colors.accent}}>
                  <div className="font-medium text-xs">1. E-SIGNATURE AUTHORIZATION</div>
                  <div className="text-[10px] opacity-70">
                    Parties agree that electronic signatures shall be deemed as equivalent to handwritten signatures.
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-transparent pl-2 py-1 rounded-md">
                  <div className="font-medium text-xs flex items-center">
                    <Zap size={9} className="mr-1 text-blue-500" />
                    2. AI-POWERED ANALYSIS
                  </div>
                  <div className="text-[10px] opacity-70">
                    Utilize AI-powered contract review tools to identify potential risks and analyze clauses.
                  </div>
                </div>

                <div>
                  <div className="font-medium text-xs">3. APPROVAL WORKFLOW</div>
                  <div className="text-[10px] opacity-70 flex items-center space-x-1">
                    <span>Legal</span>
                    <ArrowRight size={8} />
                    <span>Finance</span>
                    <ArrowRight size={8} />
                    <span>Executive</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between text-[8px] text-gray-400">
                <div>Ref: AGR-2025</div>
                <div>Secured by SignPact™</div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Document 2 - Right side, slightly behind */}
        <motion.div 
          className="absolute right-[10%] top-[20%] w-[55%] z-20 opacity-85"
          initial={{ opacity: 0, y: 60, x: 30 }}
          animate={{ opacity: 0.85, y: 20, x: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
        >
          <div className="bg-white rounded-lg shadow-lg overflow-hidden" 
               style={{
                 boxShadow: '0 10px 25px -10px rgba(0, 0, 0, 0.15)', 
                 border: '1px solid rgba(230, 230, 250, 0.6)',
               }}>
            {/* Document Header */}
            <div className="h-8 flex items-center px-3 border-b relative" 
                style={{
                  background: 'linear-gradient(to right, #f3f4f6, #eef2ff)', 
                  borderColor: 'rgba(226, 232, 240, 0.8)'
                }}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-400 to-blue-500"></div>
              <div className="flex space-x-1 mr-2">
                <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.error}}></div>
                <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.warning}}></div>
                <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.success}}></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="text-[10px] font-medium" style={{color: colors.gray700}}>NDA - SignPact</div>
              </div>
            </div>
            
            {/* Document Content - Just visual elements */}
            <div className="p-3">
              <div className="space-y-2">
                <div className="h-1.5 rounded-full w-full" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
                <div className="h-1.5 rounded-full w-4/5" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
                <div className="h-1.5 rounded-full w-3/4" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Document 3 - Left side, furthest back */}
        <motion.div 
          className="absolute left-[7%] top-[25%] w-[50%] z-10 opacity-70"
          initial={{ opacity: 0, y: 70, x: -20 }}
          animate={{ opacity: 0.7, y: 25, x: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
        >
          <div className="bg-white rounded-lg shadow-md overflow-hidden" 
               style={{
                 boxShadow: '0 8px 15px -8px rgba(0, 0, 0, 0.15)', 
                 border: '1px solid rgba(230, 230, 250, 0.5)',
               }}>
            {/* Document Header */}
            <div className="h-7 flex items-center px-2 border-b relative" 
                style={{
                  background: 'linear-gradient(to right, #f0f9ff, #e0f2fe)', 
                  borderColor: 'rgba(226, 232, 240, 0.8)'
                }}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-blue-400"></div>
              <div className="flex space-x-1 mr-1.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.error}}></div>
                <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.warning}}></div>
                <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.success}}></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="text-[9px] font-medium" style={{color: colors.gray700}}>Invoice #2023-42</div>
              </div>
            </div>
            
            {/* Document Content - Just visual elements */}
            <div className="p-2.5">
              <div className="space-y-1.5">
                <div className="h-1 rounded-full w-full" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
                <div className="h-1 rounded-full w-5/6" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Document 4 - Right corner, most blurred */}
        <motion.div 
          className="absolute right-[5%] top-[33%] w-[45%] z-5 opacity-50"
          initial={{ opacity: 0, y: 80, x: 30 }}
          animate={{ opacity: 0.5, y: 40, x: 5 }}
          transition={{ duration: 1.1, delay: 1.1 }}
        >
          <div className="bg-white rounded-lg shadow-sm overflow-hidden blur-[0.5px]" 
               style={{
                 boxShadow: '0 6px 12px -8px rgba(0, 0, 0, 0.1)', 
                 border: '1px solid rgba(230, 230, 250, 0.4)',
               }}>
            {/* Document Header */}
            <div className="h-6 flex items-center px-2 border-b relative" 
                style={{
                  background: 'linear-gradient(to right, #faf5ff, #f3e8ff)', 
                  borderColor: 'rgba(226, 232, 240, 0.8)'
                }}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-violet-500"></div>
              <div className="flex space-x-0.5 mr-1">
                <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.error}}></div>
                <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.warning}}></div>
                <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.success}}></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="text-[8px] font-medium" style={{color: colors.gray700}}>Contract Amendment</div>
              </div>
            </div>
            
            {/* Document Content - Just visual elements */}
            <div className="p-2">
              <div className="space-y-1">
                <div className="h-1 rounded-full w-full" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
                <div className="h-1 rounded-full w-3/4" style={{background: 'linear-gradient(to right, #e5e7eb, #f3f4f6)'}}></div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Bottom gradient overlay to fade content */}
      <motion.div 
        className="absolute bottom-0 left-0 w-full h-32"
        style={{
          background: `linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)`,
        }}
      />
    </motion.div>
  );
};

export default MobileHeroSection; 